/*
THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE
AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM,
DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
*/
import { inject, Injectable } from '@angular/core';
import {
  Router,
  NavigationStart,
  NavigationEnd,
  NavigationCancel,
  NavigationError,
} from '@angular/router';
import { filter } from 'rxjs/operators';
import { WaitService, LoadingSource } from './wait.service';
import { LoggerService } from './logger.service';

/**
 * Service that automatically shows loading indicator during route transitions
 * Integrates with WaitService to coordinate with other loading sources
 */
@Injectable({
  providedIn: 'root',
})
export class RouterLoadingService {
  private readonly router = inject(Router);
  private readonly waitService = inject(WaitService);
  private readonly logger = inject(LoggerService);

  constructor() {
    this.initializeRouterLoading();
  }

  /**
   * Subscribe to router events and manage loading state
   */
  private initializeRouterLoading(): void {
    // Show loading on navigation start
    this.router.events
      .pipe(filter((event) => event instanceof NavigationStart))
      .subscribe((event: NavigationStart) => {
        this.logger.debug(`Router navigation started: ${event.url}`);
        this.waitService.startProgress(LoadingSource.ROUTER);
      });

    // Hide loading on navigation end
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.logger.debug(`Router navigation completed: ${event.url}`);
        this.waitService.stopProgress(LoadingSource.ROUTER);
      });

    // Hide loading on navigation cancel
    this.router.events
      .pipe(filter((event) => event instanceof NavigationCancel))
      .subscribe((event: NavigationCancel) => {
        this.logger.debug(`Router navigation cancelled: ${event.url}`);
        this.waitService.stopProgress(LoadingSource.ROUTER);
      });

    // Hide loading on navigation error
    this.router.events
      .pipe(filter((event) => event instanceof NavigationError))
      .subscribe((event: NavigationError) => {
        this.logger.error(`Router navigation error: ${event.url}, error: ${event.error}`);
        this.waitService.stopProgress(LoadingSource.ROUTER);
      });

    this.logger.info('RouterLoadingService initialized - automatic route loading enabled');
  }
}
